import React, { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    BooleanControl,
    PlaygroundSelect,
    TextControl,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

const toastTypes = ["default", "success", "error", "warning", "info"] as const;

type ToastType = (typeof toastTypes)[number];

const DEFAULT_MESSAGE = "Something happened";

export const ToastPlayground = () => {
    const [type, setType] = useState<ToastType>("default");
    const [message, setMessage] = useState(DEFAULT_MESSAGE);
    const [preventAutoClose, setPreventAutoClose] = useState(false);

    const {
        toast: showToast,
        error: showErrorToast,
        success: showSuccessToast,
        warning: showWarningToast,
        info: showInfoToast,
    } = useToaster();

    const handleShowToast = () => {
        const content = message.trim() || DEFAULT_MESSAGE;

        switch (type) {
            case "success":
                showSuccessToast(content);
                return;
            case "warning":
                showWarningToast(content);
                return;
            case "info":
                showInfoToast(content);
                return;
            case "error":
                showErrorToast(content);
                return;
            default:
                showToast(content, undefined, { preventAutoClose });
        }
    };

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Type</h2>
                        <PlaygroundSelect<ToastType>
                            options={toastTypes}
                            value={type}
                            onChange={setType}
                        />
                        <h2 className={styles.subheading}>Message</h2>
                        <TextControl
                            label="Message"
                            value={message}
                            onChange={setMessage}
                            placeholder={DEFAULT_MESSAGE}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Prevent auto-close (default type only)"
                                checked={preventAutoClose}
                                onChange={setPreventAutoClose}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={type}
                output={JSON.stringify(
                    {
                        type,
                        message: message.trim() || DEFAULT_MESSAGE,
                        preventAutoClose:
                            type === "default" ? preventAutoClose : null,
                    },
                    null,
                    2,
                )}
            >
                <Button variant="primary" size="md" onClick={handleShowToast}>
                    Show toast
                </Button>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
